import { Tool, QuestionnaireData } from '../../types';
import { logger } from '../logger';
import { normalizeInput, areTermsRelated, extractKeywords } from './inputNormalization';
import { shouldExcludeTool } from './exclusionRules';

export function filterTools(tools: Tool[], data: Partial<QuestionnaireData>): Tool[] {
  logger.info('Starting tool filtering', {
    inputTools: tools.length,
    businessType: data.businessType,
    industry: data.industry
  });

  const normalizedIndustry = data.industry ? normalizeInput(data.industry) : '';
  const normalizedGoals = data.goals?.map(normalizeInput) || [];
  const normalizedFeatures = data.features?.map(normalizeInput) || [];
  const budgetTiers = ['free', 'low', 'medium', 'high'] as const;
  const userBudgetIndex = data.budget ? budgetTiers.indexOf(data.budget) : -1;

  return tools.filter(tool => {
    try {
      if (shouldExcludeTool(tool, data as QuestionnaireData)) {
        return false;
      }

      const businessTypeMatch = !tool.bestFor?.length ||
        tool.bestFor.includes(data.businessType!) ||
        tool.compatibleWith?.includes(data.businessSubType!) ||
        (data.businessType === 'startup' && (
          tool.bestFor.includes('small-business') ||
          tool.compatibleWith?.includes('saas')
        ));

      const industryKeywords = tool.industries?.map(normalizeInput) || [];
      const userIndustryKeywords = data.otherIndustryDetails
        ? extractKeywords(data.otherIndustryDetails)
        : [normalizedIndustry];

      const industryMatch = !tool.industries?.length ||
        industryKeywords.some(keyword =>
          userIndustryKeywords.some(userKeyword =>
            areTermsRelated(keyword, userKeyword)
          ));

      const toolGoals = tool.goals?.map(normalizeInput) || [];
      const hasMatchingGoal = !tool.goals?.length ||
        toolGoals.some(goal =>
          normalizedGoals.some(userGoal =>
            areTermsRelated(goal, userGoal)
          )
        );

      const toolFeatures = tool.features?.map(normalizeInput) || [];
      const hasMatchingFeature = !tool.features?.length ||
        toolFeatures.some(feature =>
          normalizedFeatures.some(userFeature =>
            areTermsRelated(feature, userFeature)
          )
        );

      const budgetMatch = !tool.pricing?.length ||
        tool.pricing.includes(data.budget!) ||
        (userBudgetIndex >= 0 && tool.pricing.some(price => {
          const priceIndex = budgetTiers.indexOf(price as typeof budgetTiers[number]);
          return priceIndex !== -1 && priceIndex <= userBudgetIndex;
        }));

      const matchStrength = [
        businessTypeMatch ? 1 : 0,
        industryMatch ? 0.8 : 0,
        hasMatchingGoal ? 0.7 : 0,
        hasMatchingFeature ? 0.7 : 0,
        budgetMatch ? 0.6 : 0
      ].reduce((sum, val) => sum + val, 0);

      return matchStrength >= 0.5;
    } catch (error) {
      logger.error(`Error filtering tool ${tool.name}:`, error);
      return true;
    }
  });
}